import { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDate } from "@/lib/format";

const M = (n) => `${((Number(n) || 0) / 1e6).toLocaleString("nb-NO", { maximumFractionDigits: 2 })} M kr`;
const compact = (v) => {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(n % 1e6 === 0 ? 0 : 1)} M`;
  if (Math.abs(n) >= 1e3) return `${Math.round(n / 1e3)} k`;
  return String(n);
};

export function PortfolioHistoryChart({ perspective }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/portfolio/history`, { params: perspective && perspective !== "person" ? { perspective } : {} })
      .then((r) => setData(r.data))
      .catch(() => setData({ points: [] }));
  }, [perspective]);

  const points = (data?.points || []).map((p, i) => ({ ...p, idx: i, dateLabel: formatDate(p.date) }));

  return (
    <div className="rounded-xl border border-border/60 bg-card p-6 shadow-sm" data-testid="portfolio-history-card">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">
          <TrendingUp className="h-4 w-4 text-primary" /> Verdiutvikling for beholdningen
        </h3>
        {data ? <span className="text-sm font-semibold" data-testid="portfolio-history-current">{M(data.current)}</span> : null}
      </div>

      {data === null ? (
        <Skeleton className="mt-4 h-48 rounded-lg" />
      ) : points.length < 2 ? (
        <div className="mt-4 grid h-48 place-items-center rounded-lg border border-dashed border-border text-center text-xs text-muted-foreground" data-testid="portfolio-history-empty">
          <p className="max-w-[320px]">Verdiutviklingen bygges opp etter hvert som prisede aksjetransaksjoner registreres i selskapene dine.</p>
        </div>
      ) : (
        <div className="mt-3 h-48">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
            <AreaChart data={points} margin={{ top: 6, right: 8, left: -4, bottom: 0 }}>
              <defs>
                <linearGradient id="pfFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="dateLabel" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} minTickGap={24} />
              <YAxis tickFormatter={compact} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} width={46} />
              <Tooltip
                formatter={(v) => [M(v), "Porteføljeverdi"]}
                labelFormatter={(l) => l}
                contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--popover))", color: "hsl(var(--popover-foreground))", fontSize: 12 }}
              />
              <Area type="monotone" dataKey="value" stroke="hsl(var(--chart-1))" strokeWidth={2.5} fill="url(#pfFill)" dot={{ r: 3, fill: "hsl(var(--chart-1))" }} activeDot={{ r: 5 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
